/**
 * The WRITE side of a template — the split editor (source/rich on the left, an
 * always-on live preview on the right), its mode toggle, the import dialog and
 * the preview/test-send dialog.
 *
 * Every entry point uses these: "New template" and "Import template" on the
 * Templates tab, "Write one" / "Paste HTML" / "Edit" in the campaign wizard.
 * There is deliberately no second, simpler editor anywhere.
 */
import React from 'react'
import { Button, Dialog, Field, Input } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Val } from '@/components/common'
import { useRichEditor } from '@/components/RichEditor'
import { InsertFieldButton, useHtmlSource } from '@/components/HtmlEditor'
import { createTemplate, updateTemplate } from '@/lib/db'
import { sendTestEmail } from '@/lib/api'
import { useToast } from '@/lib/toast'
import { safeIndex, stepIndex } from '@/lib/pager'
import {
  customMergeFields,
  htmlToText,
  renderEmail,
  textToHtml,
  unresolvedTokens,
  type RenderedEmail,
} from '@shared/render'
import type { Lead, Template, TemplateBodyMode } from '@shared/types'
import { SAMPLE_LEAD } from './TemplateViewer'

type Device = 'desktop' | 'mobile'

/** What a template renders as while previewing — never a real send. */
const PREVIEW_UNSUB = 'https://example.com/unsubscribe?t=preview'

/**
 * What "Write one" starts you with: a friendly, minimal RICH draft.
 *
 * Deliberately plain — rich mode is a WYSIWYG surface, so the starter is
 * something you can type over rather than a table layout the editor would have
 * to flatten. The polished "First outreach" design lives in the library
 * (supabase/seed.sql) and is reached through "Choose from templates"; pasted
 * markup keeps its own html mode via "Paste HTML".
 */
const STARTER_SUBJECT = '{{first_name}}, about your application for {{role}}'

const STARTER_TEXT = [
  'Hi {{first_name}},',
  '',
  "Thanks for applying for the {{role}} role at {{company}} \u2014 we've reviewed your application and we'd like to take the next step with you.",
  '',
  "Reply with a couple of times that work for you this week and we'll set up a short call.",
  '',
  'Warm regards,',
  'The {{company}} Team',
].join('\n')

/** ONE definition of "a new template", so every entry point agrees. */
export function createBlankTemplate(campaignId: string | null): Promise<Template> {
  return createTemplate({
    campaign_id: campaignId,
    name: 'First outreach',
    subject: STARTER_SUBJECT,
    body_text: STARTER_TEXT,
    body_html: textToHtml(STARTER_TEXT),
    body_mode: 'rich',
  })
}

/** What the editor starts from: rich html, or legacy plain text upgraded. */
export function initialTemplateHtml(t: Template): string {
  return t.body_html?.trim() ? t.body_html : textToHtml(t.body_text ?? '')
}

/** Trailing-edge debounce — keeps the preview iframe off the keystroke path. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = React.useState(value)
  React.useEffect(() => {
    if (ms <= 0) return
    const t = window.setTimeout(() => setSettled(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return ms <= 0 ? value : settled
}

/**
 * ONE renderer for the split editor's side pane, the Show-preview modal and the
 * read viewer — same merge + render call, so they can never disagree.
 */
export function useRenderedTemplate(
  draft: Pick<Template, 'id' | 'subject' | 'body_html' | 'body_text' | 'body_mode'> | null,
  lead: Pick<Lead, 'name' | 'email' | 'fields'>,
  debounceMs = 200,
): RenderedEmail | null {
  const subject = useDebounced(draft?.subject ?? '', debounceMs)
  const html = useDebounced(draft?.body_html ?? '', debounceMs)
  const text = useDebounced(draft?.body_text ?? '', debounceMs)
  const mode = draft?.body_mode
  const id = draft?.id

  return React.useMemo(
    () =>
      draft
        ? renderEmail(
            {
              subject,
              body_text: text,
              body_html: html?.trim() ? html : '',
              body_mode: mode ?? 'rich',
            },
            lead,
            PREVIEW_UNSUB,
          )
        : null,
    // id/mode stand in for `draft`; the body fields are already debounced above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, mode, subject, html, text, lead],
  )
}

/**
 * Draft state for one template: what the editor edits, whether it is dirty, and
 * the save that derives the text part. Both hosts use this so "dirty" and
 * "saved" mean the same thing in the wizard as on the Templates tab.
 */
export function useTemplateDraft(source: Template | null) {
  const [baseline, setBaseline] = React.useState<Template | null>(source)
  const [draft, setDraft] = React.useState<Template | null>(source)
  const [bodyEdited, setBodyEdited] = React.useState(false)
  const [saving, setSaving] = React.useState(false)

  /** Point the editor at a template, discarding anything in flight. */
  const select = React.useCallback((t: Template | null) => {
    setBaseline(t)
    setDraft(t)
    setBodyEdited(false)
  }, [])

  // Follow the host's selection. A reload of the SAME template keeps in-flight
  // edits — only a different id re-points the editor.
  React.useEffect(() => {
    if (source?.id !== baseline?.id) select(source)
  }, [source, baseline?.id, select])

  const mode: TemplateBodyMode = draft?.body_mode ?? 'rich'

  const dirty =
    !!draft &&
    !!baseline &&
    (baseline.subject !== draft.subject || bodyEdited || baseline.body_mode !== draft.body_mode)

  function setSubject(v: string) {
    setDraft((d) => (d ? { ...d, subject: v } : d))
  }

  function setBody(html: string) {
    setDraft((d) => (d ? { ...d, body_html: html } : d))
    setBodyEdited(true)
  }

  /**
   * Rich -> HTML just reveals the current markup, so it is lossless.
   * HTML -> rich has to parse through the TipTap schema, which drops tables and
   * most inline styles, so it asks first.
   */
  function switchMode(next: TemplateBodyMode) {
    if (!draft || next === draft.body_mode) return
    if (next === 'rich') {
      const warn =
        'Switching to the rich editor re-parses your markup. Tables and most inline styles will be simplified or stripped. Continue?'
      if (!window.confirm(warn)) return
    }
    setDraft({ ...draft, body_mode: next })
    setBodyEdited(true)
  }

  function revert() {
    setDraft(baseline)
    setBodyEdited(false)
  }

  /**
   * In html mode the author's markup is stored byte-for-byte; the text/plain
   * part is still derived from it so both parts stay in sync. Throws on
   * failure — the host owns the message.
   */
  async function save(): Promise<Template | null> {
    if (!draft) return null
    setSaving(true)
    try {
      const html = draft.body_html?.trim() ? draft.body_html : textToHtml(draft.body_text ?? '')
      const saved = await updateTemplate(draft.id, {
        subject: draft.subject,
        body_html: html,
        body_text: htmlToText(html),
        body_mode: draft.body_mode,
      })
      select(saved)
      return saved
    } finally {
      setSaving(false)
    }
  }

  return { draft, baseline, dirty, mode, saving, select, setSubject, setBody, switchMode, revert, save }
}

// --- split panes ---------------------------------------------------------------

/**
 * 38px pane header strip, matching on both sides of the split.
 *
 * `grow` is for the rich-text pane: its toolbar WRAPS, and a fixed 38px strip
 * clipped every row after the first — which hid undo/redo, the font controls,
 * the mark buttons and Insert field entirely at 1440px. With grow the strip
 * takes the height its content needs, at every width.
 */
function PaneHeader({ children, grow }: { children: React.ReactNode; grow?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        ...(grow
          ? { minHeight: 38, padding: '5px 10px', flexWrap: 'wrap' as const, rowGap: 6 }
          : { height: 38, padding: '0 10px', overflowX: 'auto' as const }),
        flex: 'none',
        background: 'var(--surface-box-header)',
        borderBottom: '1px solid var(--border-default)',
      }}
    >
      {children}
    </div>
  )
}

function PaneLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--text-faint)',
        textTransform: 'uppercase',
        letterSpacing: '.05em',
        whiteSpace: 'nowrap',
        flex: 'none',
      }}
    >
      {children}
    </span>
  )
}

/** Small uppercase label + full-pane-width input. */
function SubjectField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ flex: 'none' }}>
      <PaneLabel>Subject</PaneLabel>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="{{first_name}}, about your application for {{role}}"
        style={{
          display: 'block',
          width: '100%',
          marginTop: 4,
          padding: 8,
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          borderRadius: 6,
          outline: 'none',
          fontFamily: 'var(--font-sans)',
          fontSize: 14,
          color: 'var(--text-primary)',
          boxSizing: 'border-box',
        }}
      />
    </div>
  )
}

const paneBody: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: 12,
}

interface PaneProps {
  subject: string
  value: string
  mergeFields: string[]
  onSubject: (v: string) => void
  onBody: (html: string) => void
}

/** HTML mode: Insert-field in the header, verbatim source textarea in the body. */
function HtmlPane({ subject, value, mergeFields, onSubject, onBody }: PaneProps) {
  const src = useHtmlSource({ value, onChange: onBody })
  return (
    <>
      <PaneHeader>
        <PaneLabel>HTML source</PaneLabel>
        <div style={{ flex: 1 }} />
        <InsertFieldButton mergeFields={mergeFields} onInsert={src.insertToken} />
      </PaneHeader>
      <div style={paneBody}>
        <SubjectField value={subject} onChange={onSubject} />
        {src.area}
      </div>
    </>
  )
}

/** Rich mode: the TipTap toolbar lives in the header strip. */
function RichPane({ subject, value, mergeFields, onSubject, onBody }: PaneProps) {
  const rich = useRichEditor({
    initialHtml: value,
    mergeFields,
    placeholder: 'Write your email…',
    onChange: onBody,
  })
  return (
    <>
      {/* grow: the toolbar wraps, and a fixed-height strip clips every row
          after the first. */}
      <PaneHeader grow>
        <PaneLabel>Rich text</PaneLabel>
        {rich.toolbar}
      </PaneHeader>
      <div style={paneBody}>
        <SubjectField value={subject} onChange={onSubject} />
        {rich.insertRow}
        {rich.surface}
      </div>
    </>
  )
}

/**
 * Steps the preview through the campaign's leads.
 *
 * Wraps at both ends on purpose: with 12 leads, "next" from #12 is #1. A
 * disabled arrow at each end would make the pager feel broken on a short list.
 */
function LeadPager({
  index,
  total,
  onPrev,
  onNext,
}: {
  index: number
  total: number
  onPrev: () => void
  onNext: () => void
}) {
  const arrow: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    border: '1px solid var(--border-default)',
    borderRadius: 6,
    background: 'var(--surface-card)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    flex: 'none',
  }
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none' }}>
      <button type="button" title="Previous lead" aria-label="Previous lead" style={arrow} onClick={onPrev}>
        <Ic.chevronLeft size={13} />
      </button>
      <span
        className="tnum"
        aria-live="polite"
        style={{ fontSize: 11.5, color: 'var(--text-muted)', minWidth: 38, textAlign: 'center' }}
      >
        {index + 1} / {total}
      </span>
      <button type="button" title="Next lead" aria-label="Next lead" style={arrow} onClick={onNext}>
        <Ic.chevronRight size={13} />
      </button>
    </div>
  )
}

/** Desktop | Mobile width switch. */
function DeviceToggle({ device, onChange }: { device: Device; onChange: (d: Device) => void }) {
  const btn = (d: Device, label: string) => (
    <button
      type="button"
      aria-pressed={device === d}
      onClick={() => onChange(d)}
      style={{
        height: 22,
        padding: '0 8px',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: 11.5,
        fontWeight: 600,
        background: device === d ? 'var(--surface-card)' : 'transparent',
        color: device === d ? 'var(--accent-text)' : 'var(--text-muted)',
      }}
    >
      {label}
    </button>
  )
  return (
    <span
      style={{
        display: 'inline-flex',
        gap: 2,
        padding: 2,
        borderRadius: 6,
        background: 'var(--surface-sunken)',
        flex: 'none',
      }}
    >
      {btn('desktop', 'Desktop')}
      {btn('mobile', 'Mobile')}
    </span>
  )
}

/** Rich editor / HTML source switch, shown next to Show preview. */
export function TemplateModeToggle({
  mode,
  onSwitch,
}: {
  mode: TemplateBodyMode
  onSwitch: (m: TemplateBodyMode) => void
}) {
  const btn = (m: TemplateBodyMode, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      aria-pressed={mode === m}
      onClick={() => onSwitch(m)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 26,
        padding: '0 9px',
        border: 'none',
        borderRadius: 5,
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: 12.5,
        fontWeight: 600,
        background: mode === m ? 'var(--surface-card)' : 'transparent',
        color: mode === m ? 'var(--accent-text)' : 'var(--text-muted)',
      }}
    >
      {icon}
      {label}
    </button>
  )
  return (
    <span
      style={{
        display: 'inline-flex',
        gap: 2,
        padding: 2,
        borderRadius: 6,
        background: 'var(--surface-sunken)',
        marginRight: 4,
      }}
    >
      {btn('rich', 'Rich editor', <Ic.pencil size={13} />)}
      {btn('html', 'HTML', <Ic.code size={13} />)}
    </span>
  )
}

/**
 * The editor itself: source or rich text on the left, an always-on rendered
 * preview on the right. The host supplies the chrome (title, Save, Cancel) —
 * the Templates tab puts it in the topbar, the wizard in its own bar.
 */
export function TemplateSplitEditor({
  draft,
  mergeFields,
  sampleLead,
  leads,
  narrow,
  onSubject,
  onBody,
  style,
}: {
  draft: Template
  mergeFields: string[]
  sampleLead: Pick<Lead, 'name' | 'email' | 'fields'>
  /**
   * The leads the preview pages through, in the same order the Leads step shows
   * them. Absent or empty keeps the single sampleLead and hides the pager —
   * which is the Templates tab opened with no leads to page through.
   */
  leads?: Pick<Lead, 'name' | 'email' | 'fields'>[]
  narrow?: boolean
  onSubject: (v: string) => void
  onBody: (html: string) => void
  style?: React.CSSProperties
}) {
  const [device, setDevice] = React.useState<Device>('desktop')

  // The Insert-field menu offers the host's fields PLUS every custom token the
  // draft itself already merges — so a token added via "Add custom field…"
  // (or typed by hand) stays in the menu before the template is ever saved.
  // Inserting a token IS its registration; there is no other registry.
  const menuFields = React.useMemo(() => {
    const extra = customMergeFields(draft).filter((t) => !mergeFields.includes(t))
    return extra.length ? [...mergeFields, ...extra] : mergeFields
  }, [mergeFields, draft])

  // The pager walks `leads`; with none it degrades to the single sample lead.
  const roster = leads && leads.length ? leads : [sampleLead]
  const [leadIndex, setLeadIndex] = React.useState(0)
  // A shorter roster (the campaign changed underneath us) must not strand the
  // index past the end.
  const index = safeIndex(leadIndex, roster.length)
  const previewedLead = roster[index] ?? sampleLead
  const step = (delta: number) => setLeadIndex((i) => stepIndex(i, delta, roster.length))

  const livePreview = useRenderedTemplate(draft, previewedLead)
  // Exactly what the send-path guard would report for THIS recipient, so an
  // unresolved token is shown rather than quietly rendering blank.
  const missing = React.useMemo(
    () => unresolvedTokens(draft, { lead: previewedLead }),
    [draft, previewedLead],
  )

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: narrow ? 'column' : 'row',
        ...style,
      }}
    >
      {/* ---- LEFT: editor -------------------------------------------------- */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: narrow ? 'none' : '1px solid var(--border-default)',
          borderBottom: narrow ? '1px solid var(--border-default)' : 'none',
        }}
      >
        {/* keyed so switching template or mode builds a fresh editor */}
        {draft.body_mode === 'html' ? (
          <HtmlPane
            key={`${draft.id}-html`}
            subject={draft.subject}
            value={initialTemplateHtml(draft)}
            mergeFields={menuFields}
            onSubject={onSubject}
            onBody={onBody}
          />
        ) : (
          <RichPane
            key={`${draft.id}-rich`}
            subject={draft.subject}
            value={initialTemplateHtml(draft)}
            mergeFields={menuFields}
            onSubject={onSubject}
            onBody={onBody}
          />
        )}
      </div>

      {/* ---- RIGHT: live preview ------------------------------------------- */}
      <div
        style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <PaneHeader>
          <PaneLabel>Live preview</PaneLabel>
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontSize: 11.5,
              color: 'var(--text-muted)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            as {previewedLead.email}
          </span>
          {roster.length > 1 && (
            <LeadPager
              index={index}
              total={roster.length}
              onPrev={() => step(-1)}
              onNext={() => step(1)}
            />
          )}
          <DeviceToggle device={device} onChange={setDevice} />
        </PaneHeader>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            background: 'var(--surface-preview)',
            padding: 14,
          }}
        >
          {/* The same finding the wizard blocks on and the sender fails a row
              for — surfaced here so a blank in the preview is never a mystery. */}
          {missing.length > 0 && (
            <div
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
                marginBottom: 10,
                padding: '6px 9px',
                borderRadius: 8,
                background: 'var(--status-failed-bg)',
                color: 'var(--status-failed-fg)',
                fontSize: 11.5,
                fontWeight: 500,
              }}
            >
              <Ic.warning size={12} />
              <span>
                {missing.map((m) => `{{${m.token}}}`).join(', ')}{' '}
                {missing.length === 1 ? 'is' : 'are'} empty for this lead — it would be skipped
              </span>
            </div>
          )}

          {livePreview && (
            <>
              <div
                style={{
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  marginBottom: 10,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Subject:{' '}
                <strong style={{ color: 'var(--text-primary)' }}>
                  {livePreview.subject || '(no subject)'}
                </strong>
              </div>
              <div
                style={{
                  maxWidth: device === 'desktop' ? 600 : 360,
                  margin: '0 auto',
                  background: '#fff',
                  border: '1px solid var(--border-default)',
                  borderRadius: 6,
                  overflow: 'hidden',
                  boxShadow: '0 1px 2px rgba(9,9,11,.06)',
                  transition: 'max-width var(--dur-base) var(--ease-out)',
                }}
              >
                <iframe
                  title="Live email preview"
                  // sandbox="" — no scripts, no forms, no navigation.
                  sandbox=""
                  srcDoc={livePreview.html}
                  style={{ display: 'block', width: '100%', height: 640, border: 'none' }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Import a polished HTML email. The pasted markup goes in verbatim as an
 * html-mode template — the same storage path as the HTML editor, so tables and
 * inline styles are never re-parsed.
 */
export function ImportTemplateDialog({
  busy,
  onClose,
  onImport,
}: {
  busy: boolean
  onClose: () => void
  onImport: (input: { name: string; subject: string; html: string }) => void
}) {
  const toast = useToast()
  const [name, setName] = React.useState('')
  const [subject, setSubject] = React.useState('')
  const [html, setHtml] = React.useState('')
  const [fileName, setFileName] = React.useState('')
  const fileRef = React.useRef<HTMLInputElement>(null)

  async function readFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      setHtml(text)
      setFileName(file.name)
      // A filename is a decent default label when none was typed yet.
      if (!name.trim()) setName(file.name.replace(/\.html?$/i, ''))
    } catch (err) {
      toast('error', "Couldn't read that file", err instanceof Error ? err.message : String(err))
    }
  }

  function submit() {
    if (!name.trim()) {
      toast('error', 'Give the template a name')
      return
    }
    if (!subject.trim()) {
      toast('error', 'Subject is required')
      return
    }
    if (!html.trim()) {
      toast('error', 'Paste the email HTML, or upload a .html file')
      return
    }
    onImport({ name, subject, html })
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={620}
      title="Import template"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={submit}>
            {busy ? 'Importing…' : 'Import template'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Name" hint="Shown in the template list">
            <Input
              value={name}
              onChange={setName}
              placeholder="Shortlisted — July intake"
              autoFocus
            />
          </Field>
          <Field label="Subject" hint="What recipients see">
            <Input
              value={subject}
              onChange={setSubject}
              placeholder="{{first_name}}, about your application"
            />
          </Field>
        </div>

        <Field
          label="Email HTML"
          hint="Pasted exactly as-is — tables and inline styles are preserved. {{tokens}} still merge."
        >
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
            wrap="off"
            rows={12}
            placeholder={'<table width="600" cellpadding="0" cellspacing="0">…'}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 12px',
              border: '1px solid var(--border-default)',
              borderRadius: 6,
              outline: 'none',
              resize: 'vertical',
              background: 'var(--surface-card)',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              fontSize: 12,
              lineHeight: 1.6,
              whiteSpace: 'pre',
              overflow: 'auto',
              boxSizing: 'border-box',
            }}
          />
        </Field>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            ref={fileRef}
            type="file"
            accept=".html,.htm,text/html"
            onChange={readFile}
            style={{ display: 'none' }}
          />
          <Button
            variant="secondary"
            size="sm"
            icon={<Ic.upload size={14} />}
            onClick={() => fileRef.current?.click()}
          >
            Upload .html file
          </Button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {fileName || 'or paste the markup above'}
          </span>
        </div>
      </div>
    </Dialog>
  )
}

/** The rendered email in a sandboxed frame — script-dead, styled like an inbox. */
function EmailFrame({
  rendered,
  width = '100%',
  height = 420,
}: {
  rendered: RenderedEmail
  width?: number | string
  height?: number
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-sm)',
        overflow: 'hidden',
        width,
        maxWidth: '100%',
        margin: typeof width === 'number' ? '0 auto' : undefined,
        transition: 'width var(--dur-base) var(--ease-out)',
      }}
    >
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-default)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          {rendered.subject || '(no subject)'}
        </div>
      </div>
      <iframe
        title="Rendered email preview"
        sandbox=""
        srcDoc={rendered.html}
        style={{ display: 'block', width: '100%', height, border: 'none', background: '#fff' }}
      />
    </div>
  )
}

/** Show preview + send a real test to yourself, from either entry point. */
export function TemplatePreviewDialog({
  draft,
  sampleLead,
  canSend,
  campaignId,
  onClose,
}: {
  draft: Template
  sampleLead?: Pick<Lead, 'name' | 'email' | 'fields'>
  canSend: boolean
  campaignId: string
  onClose: () => void
}) {
  const toast = useToast()
  const [device, setDevice] = React.useState<Device>('desktop')
  const [sending, setSending] = React.useState(false)
  const lead = sampleLead ?? SAMPLE_LEAD
  const rendered = useRenderedTemplate(draft, lead, 0)

  async function sendTest() {
    setSending(true)
    try {
      const html = draft.body_html?.trim() ? draft.body_html : textToHtml(draft.body_text ?? '')
      const result = await sendTestEmail({
        subject: draft.subject,
        body_html: html,
        body_text: htmlToText(html),
        body_mode: draft.body_mode,
        campaign_id: campaignId || undefined,
      })
      toast(
        result.sent ? 'success' : 'info',
        result.sent ? 'Test email sent' : 'Dry run',
        result.detail,
      )
    } catch (e) {
      toast('error', 'Test send failed', e instanceof Error ? e.message : String(e))
    } finally {
      setSending(false)
    }
  }

  const deviceButton = (kind: Device, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      title={label}
      aria-pressed={device === kind}
      onClick={() => setDevice(kind)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 26,
        padding: '0 10px',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'var(--font-sans)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: device === kind ? 'var(--accent-soft)' : 'transparent',
        color: device === kind ? 'var(--accent-text)' : 'var(--text-secondary)',
      }}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <Dialog
      open
      onClose={onClose}
      width={760}
      title="Preview"
      footer={
        <>
          {!canSend && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
              Add an SMTP identity (with a password) to send a test.
            </span>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button icon={<Ic.send size={14} />} disabled={!canSend || sending} onClick={sendTest}>
            {sending ? 'Sending…' : 'Send test email'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Values from <Val size={12}>{lead.email}</Val>
          </span>
          <div style={{ flex: 1 }} />
          {deviceButton('desktop', <Ic.desktop size={13} />, 'Desktop')}
          {deviceButton('mobile', <Ic.mobile size={13} />, 'Mobile')}
        </div>
        {rendered && (
          <EmailFrame rendered={rendered} width={device === 'desktop' ? 640 : 375} height={440} />
        )}
      </div>
    </Dialog>
  )
}
